import { useCallback, useMemo, useState } from 'react'
import type { BootstrapMode } from './useBootstrapMode'

const AI_USAGE_TOTAL_KEY = 'joggo_ai_usage_total_count'

/**
 * シミュレータ利用状況の計測カスタムフック
 *
 * 計測内容:
 * - AI利用回数
 * - AI利用回数累計
 * - 編集バリデーションエラー発生回数（ロゴ・刻印・色指定漏れ）
 * - マイデザイン保存フラグ
 * - マイデザインからの読み込みフラグ
 */
export function useSimulatorAnalytics(mode: BootstrapMode) {
  // AI利用回数
  const [aiUsedCount, setAiUsedCount] = useState(0)

  // バリデーションエラー発生回数（種類別）
  const [logoValidationErrorCount, setLogoValidationErrorCount] = useState(0)
  const [engravingValidationErrorCount, setEngravingValidationErrorCount] =
    useState(0)
  const [emptyColorValidationErrorCount, setEmptyColorValidationErrorCount] =
    useState(0)

  // マイデザインに保存したか
  const [savedToMyDesign, setSavedToMyDesign] = useState(false)

  // マイデザインから読み込んだか
  const [loadedFromMyDesign, setLoadedFromMyDesign] = useState(false)

  /**
   * AI利用回数をインクリメント
   * １セッションあたりの利用回数と累計（LocalStorage）の両方を更新
   */
  const trackAIUsage = useCallback(() => {
    setAiUsedCount((prev) => {
      return prev + 1
    })

    // LocalStorageの累計カウントも更新
    if (typeof window === 'undefined') {
      return
    }

    try {
      const currentTotal = parseInt(
        localStorage.getItem(AI_USAGE_TOTAL_KEY) || '0',
        10
      )
      const newTotal = currentTotal + 1
      localStorage.setItem(AI_USAGE_TOTAL_KEY, String(newTotal))
    } catch {
      // LocalStorageアクセス失敗時は累計カウントなしで続行
    }
  }, [])

  /**
   * マイデザイン保存フラグを設定
   */
  const trackDesignSaved = useCallback(() => {
    setSavedToMyDesign(true)
  }, [])

  /**
   * マイデザイン読み込みフラグを設定
   * MyDesignListPanelからデザインを適用した時に呼び出す
   */
  const trackLoadedFromMyDesign = useCallback(() => {
    setLoadedFromMyDesign(true)
  }, [])

  /**
   * バリデーションエラーを処理して適切にカウント
   * エラー種別に応じて重複なく各カウンターをインクリメント
   */
  const trackValidationErrors = useCallback(
    (errors: Array<{ type: string }>) => {
      let hasLogoError = false
      let hasEngravingError = false
      let hasColorError = false

      errors.forEach((error) => {
        switch (error.type) {
          case 'logo_not_selected':
            hasLogoError = true
            break
          case 'name_not_selected':
          case 'name_text_empty':
          case 'name_text_invalid':
            hasEngravingError = true
            break
          case 'parts_not_selected':
            hasColorError = true
            break
        }
      })

      if (hasLogoError) {
        setLogoValidationErrorCount((prev) => {
          return prev + 1
        })
      }
      if (hasEngravingError) {
        setEngravingValidationErrorCount((prev) => {
          return prev + 1
        })
      }
      if (hasColorError) {
        setEmptyColorValidationErrorCount((prev) => {
          return prev + 1
        })
      }
    },
    []
  )

  /**
   * 計測データを取得
   * GTMイベント送信時に使用
   */
  const getTrackingData = useCallback(() => {
    let aiUsedCountTotal = 0

    // LocalStorageから累計を取得（SSR/ITP/プライベートモード対応）
    if (typeof window !== 'undefined') {
      try {
        aiUsedCountTotal = parseInt(
          localStorage.getItem(AI_USAGE_TOTAL_KEY) || '0',
          10
        )
      } catch {
        // LocalStorageアクセス失敗時は0
      }
    }

    return {
      aiUsedCount,
      aiUsedCountTotal,
      logoValidationErrorCount,
      engravingValidationErrorCount,
      emptyColorValidationErrorCount,
      loadedFromMyDesign,
      savedToMyDesign
    }
  }, [
    aiUsedCount,
    logoValidationErrorCount,
    engravingValidationErrorCount,
    emptyColorValidationErrorCount,
    loadedFromMyDesign,
    savedToMyDesign
  ])

  /**
   * AI利用回数累計をリセット
   * カート追加/更新成功後にGTMへデータを送信した後に呼び出す
   */
  const resetAIUsageTotalCount = useCallback(() => {
    if (typeof window === 'undefined') {
      return
    }

    try {
      localStorage.setItem(AI_USAGE_TOTAL_KEY, '0')
    } catch {
      // LocalStorageアクセス失敗時は無視
    }
  }, [])

  return {
    trackAIUsage,
    trackValidationErrors,
    trackDesignSaved,
    trackLoadedFromMyDesign,
    getTrackingData,
    resetAIUsageTotalCount
  }
}
